import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user?.name || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleEdit = () => {
    setEditing(true);
    setError('');
    setSuccess(false);
  };

  const handleCancel = () => {
    setEditing(false);
    setName(user?.name || '');
    setError('');
    setSuccess(false);
  };

  const handleSave = async () => {
    if (!name.trim() || name.length < 2) {
      setError('Name must be at least 2 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    const result = await updateProfile(name.trim());
    
    if (result.success) {
      setSuccess(true);
      setEditing(false);
      setTimeout(() => setSuccess(false), 3000);
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Container maxWidth="sm" sx={{ pt: 3, pb: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" component="h1" fontWeight="bold">
          Profile
        </Typography>
        <IconButton 
          onClick={() => navigate('/')}
          aria-label="close"
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mt: 1 }}>
        {/* Profile Header */}
        <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
          <Avatar
            sx={{
              width: 80,
              height: 80,
              bgcolor: 'primary.main',
              fontSize: '2rem',
              mb: 2,
            }}
          >
            {user?.name ? getInitials(user.name) : <PersonIcon />}
          </Avatar>
          <Typography variant="h6" fontWeight="bold">
            {user?.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Profile updated successfully!
          </Alert>
        )}

        {/* Email Field (Read-only) */}
        <TextField
          fullWidth
          label="Email"
          value={user?.email || ''}
          margin="normal"
          InputProps={{
            readOnly: true,
            startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
          }}
          helperText="Email cannot be changed"
        />

        {/* Name Field */}
        <TextField
          fullWidth
          label="Full Name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError('');
            setSuccess(false);
          }}
          margin="normal"
          InputProps={{
            readOnly: !editing,
            startAdornment: <PersonIcon color="action" sx={{ mr: 1 }} />,
          }}
          required
        />

        {/* Member Since */}
        <TextField
          fullWidth
          label="Member Since"
          value={user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
          margin="normal"
          InputProps={{
            readOnly: true,
          }}
        />

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {!editing ? (
            <Button
              variant="contained"
              fullWidth
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit Profile
            </Button>
          ) : (
            <>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={loading || !name.trim()}
              >
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </>
          )}
          {/* Allow only authenticated users to register new users */}
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate('/register')}
          >
            Register New User
          </Button>
        </Box>

        {/* Account Info */}
        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="bold">
            Account Information
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            User ID: {user?.id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last updated: {user?.updatedAt ? new Date(user.updatedAt).toLocaleString() : 'N/A'}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;